// Parallel flag evaluation, the Tanstack Query key for the per-Club flags
// cache, the shell's cache-warming policy, and the fn's no-session guard.
// Extracted from the `.fn.ts` wrapper and the route loader (both
// coverage-excluded) so the logic stays unit-testable.

import type { QueryClient } from '@tanstack/react-query';

import { BOOLEAN_FLAG_DEFAULTS, FLAG_KEYS, type FlagKey } from './flags';
import type { FeatureFlags } from './ports';

/**
 * Query key for the per-Club feature-flag map. Partitioned by `clubId` so two
 * Clubs never share one cache entry (the live-in-club precedent), even though
 * the server fn resolves its own scope from the request.
 */
export function featureFlagsQueryKey(clubId: string) {
	return ['feature-flags', clubId] as const;
}

/**
 * Evaluate every known flag for one Club in PARALLEL. The prior authenticated-
 * shell loop was serial — each Flagship round-trip blocked the next — so the
 * whole map cost the sum of every flag's latency. `Promise.all` overlaps them:
 * the map costs one round-trip's latency, not the sum. Each read is total (the
 * port resolves to the supplied default on any provider error), so one bad flag
 * can't reject the batch.
 */
export async function evaluateAllFlags(
	featureFlags: FeatureFlags,
	clubId: string,
): Promise<Record<FlagKey, boolean>> {
	const entries = await Promise.all(
		FLAG_KEYS.map(
			async (key) =>
				[
					key,
					await featureFlags.getBoolean(key, BOOLEAN_FLAG_DEFAULTS[key], {
						clubId,
					}),
				] as const,
		),
	);
	return Object.fromEntries(entries) as Record<FlagKey, boolean>;
}

/**
 * Thrown by the flags server fn when the request carries no authenticated
 * session. Unauthenticated is a FAILURE, not a dark map: the fn's result
 * lands in a CACHEABLE position (the per-Club flags query, staleTime 60s),
 * so a transient no-session response returned as `{...defaults}` could be
 * cached under the real Club's key and pin every flag dark for a minute.
 * A rejection caches nothing — callers fall back to per-key defaults for
 * the render and the next fetch retries.
 */
export class UnauthenticatedFlagsError extends Error {
	constructor() {
		super('Cannot evaluate feature flags: no authenticated session.');
		this.name = 'UnauthenticatedFlagsError';
	}
}

/**
 * The fn's no-session guard: narrows a nullable tenant scope, throwing
 * `UnauthenticatedFlagsError` when it is absent.
 */
export function assertFlagsScope<T extends { clubId: string }>(
	scope: T | null | undefined,
): T {
	if (!scope) throw new UnauthenticatedFlagsError();
	return scope;
}

/**
 * The authenticated shell's flags cache-warming policy, shared by the
 * `_authenticated` route loader. Invariant: **the authenticated shell never
 * renders without a real flag map** —
 *
 * - Server (SSR): awaited, so the map dehydrates through
 *   `routerWithQueryClient` and hydration has no flash.
 * - Client, COLD cache: awaited too — exactly once per session, right after
 *   login (~100-300ms), so the first client-side entry paints the nav from
 *   real flags instead of the dark defaults.
 * - Client, warm cache: `ensureQueryData` returns the cached map
 *   synchronously and the nav pays nothing.
 *
 * A rejected evaluation (e.g. the fn's no-session throw) caches NOTHING
 * under the Club key, never surfaces as an unhandled rejection, and never
 * fails the route load: the nav falls back to per-key defaults for that
 * render and the still-cold cache retries on the next navigation.
 */
export async function warmFeatureFlags(
	queryClient: QueryClient,
	clubId: string,
	fetchFlags: () => Promise<Record<FlagKey, boolean>>,
	isServer: boolean = typeof window === 'undefined',
): Promise<void> {
	const queryKey = featureFlagsQueryKey(clubId);
	const cold = queryClient.getQueryData(queryKey) === undefined;
	// `revalidateIfStale`: `ensureQueryData` alone never refetches once a value
	// is cached, which would make the 60s staleTime inert — the map would go
	// stale forever in a long-lived session. With it a warm nav still returns
	// the cached map synchronously while a stale one refetches in the
	// background, so flag changes (including the `live-stream` kill switch)
	// land on the next navigation.
	const flags = queryClient
		.ensureQueryData({
			queryKey,
			queryFn: fetchFlags,
			staleTime: 60_000,
			revalidateIfStale: true,
		})
		.catch(() => undefined);
	if (isServer || cold) {
		await flags;
	}
}
